/**
 * 67.56: the armour's look, round two (the owner, Oct 18: "For armor I like plate carrier and flak is cool too to keep
 * as an option, but we can stick with plate carrier for now. In general the difference between helmet and no helmet
 * seems to not be clear. We should expect to be able to do helmet + something else. For no-helmet a face would be
 * annoying so could just do a balaclava and keep it simple." … "it probably makes sense to align with armor system so
 * light helmet, heavy helmet, squad leader variant for both").
 *
 * Switched on (the owner, Oct 18, on the two questions: "1 sure no armor 2. real piece of armor"): `buildPersonModel`
 * dresses every person through `dressArmour` from its `PersonKit` (the far tier's poses are personModel.ts's own), and
 * `drawnHelmet` is the host's rule for which helmet a unit wears. A person wears a
 * **helmet** (none, light, heavy) and a **vest** (none, light, heavy) that combine freely, in the **plate carrier** look
 * (B, the pick) or the **flak** look (D, kept as the option), and optionally as the **squad leader** (68.8's kit without
 * its helmet stripe and chevron, and the leader's variant of whichever helmet is worn). Every piece goes on a group that
 * already moves (the head, the torso, the arms), in the person's flat Lambert, so it bakes into that group's draw:
 * **no new draws**. The far tier (personModel.ts) carries the same head, vest and leader marks in every pose.
 *
 * - **No helmet** is a balaclava: a knit hood over the head with an eye slit, no visor — the head small and round and
 *   without the flash band across the eyes, where a helmet is a dark dome over a bright visor.
 * - **Light helmet** (B): the old shell's size (every figure measures as before), a cover band, rails, a front mount, a
 *   counterweight pouch with a strobe.
 *   **Heavy helmet** (B): full-cut over the ears with a mandible guard. (D: a covered helmet with goggles on the band; a
 *   pot helmet with its face shield raised.)
 * - **The leader's variant** of a helmet (subtle): its cover band in the team's flash, a boom mic, a short antenna on the
 *   support-side rail, two rank bars on the back; on a heavy helmet the mandible guard's edge in the flash too.
 * - **Vests** (B): a plate carrier — plate bags, a cummerbund, the magazine shingle in the team's colour, a team patch;
 *   heavy adds a throat guard, side plates, a groin flap and soft deltoid pads. (D: a quilted flak vest; heavy, a long
 *   padded vest with an apron and quilted shoulders.)
 *
 * Colour schemes (`ArmourScheme`): `dark` (the team's dark — B's default), `body` (the team's body colour), `issue` (a
 * neutral olive — D's default). Wear (`ArmourWear`) is what 185's life reads as: `whole`, `worn` (below half: scuffs, a
 * strap loose), `broken` (spent, still worn: cracked plates, a split helmet, a shattered visor). In play a view would
 * rebuild at those two marks only — twice a life at most.
 *
 * Rendering only: no game state is read or written. Seeded (mulberry32), so the same figure scuffs the same way.
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import type { PersonModel } from './personModel';
import type { UnitPalette } from './unitPalette';
import { dressLeaderKit } from './leaderLooks';
import type { ArmourWorn } from '../types';

export type HelmetKind = 'none' | 'light' | 'heavy';
export type VestKind = 'none' | 'light' | 'heavy';
export type VestLook = 'B' | 'D';
export type ArmourWear = 'whole' | 'worn' | 'broken';
export type ArmourScheme = 'dark' | 'body' | 'issue';

export const VEST_LOOKS: Record<VestLook, { name: string; scheme: ArmourScheme }> = {
  B: { name: 'plate carrier', scheme: 'dark' },
  D: { name: 'flak', scheme: 'issue' },
};
/** No helmet: the balaclava's knit and the skin in its eye slit (team-neutral, so the head reads as uncovered by a helmet). */
export const BALACLAVA = { knit: '#4b4c44', skin: '#a47c5e' };

/** The colours a look is drawn in: the shell or plate, the trim round it, the soft (cloth) parts, the pouches, the patch. */
export interface ArmourColours { plate: string; heavyPlate: string; trim: string; soft: string; pouch: string; patch: string; scuff: string; crack: string }

const ISSUE = '#5c5e48', ISSUE_DARK = '#3b3d2f';
function mix(a: string, b: string, t: number): string { return '#' + new THREE.Color(a).lerp(new THREE.Color(b), t).getHexString(); }

export function armourColours(p: UnitPalette, scheme: ArmourScheme): ArmourColours {
  const base = scheme === 'dark' ? { plate: p.dark, heavyPlate: p.metal, trim: p.metal, soft: mix(p.dark, p.body, 0.3), pouch: p.body }
    : scheme === 'body' ? { plate: p.body, heavyPlate: mix(p.body, p.dark, 0.3), trim: p.dark, soft: p.dark, pouch: p.dark }
    : { plate: ISSUE, heavyPlate: ISSUE_DARK, trim: ISSUE_DARK, soft: mix(ISSUE, ISSUE_DARK, 0.5), pouch: mix(ISSUE, p.dark, 0.35) };
  return { ...base, patch: p.flash, scuff: mix(base.plate, '#c9c2b2', 0.5), crack: '#141414' };
}

// ---- the parts (person units; a person is 1.8) --------------------------------------------------------------------

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
function shell(parent: THREE.Object3D, r: number, color: string, y: number, z = -0.01, down = 0): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.SphereGeometry(r, 7, 4, 0, Math.PI * 2, 0, Math.PI / 2 + down), mat(color));
  m.position.set(0, y, z);
  parent.add(m);
  return m;
}
function band(parent: THREE.Object3D, r: number, tube: number, color: string, y: number, z = -0.01): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.TorusGeometry(r, tube, 3, 12), mat(color));
  m.position.set(0, y, z); m.rotation.x = Math.PI / 2;
  parent.add(m);
  return m;
}
/** A box `thick` square from `a` to `b` (person units). */
function bar(parent: THREE.Object3D, a: [number, number, number], b: [number, number, number], thick: number, color: string): THREE.Mesh {
  const va = new THREE.Vector3(...a), vb = new THREE.Vector3(...b), d = vb.clone().sub(va);
  const m = new THREE.Mesh(new THREE.BoxGeometry(thick, d.length() + thick * 0.5, thick), mat(color));
  m.position.copy(va).addScaledVector(d, 0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
  parent.add(m);
  return m;
}
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

/** Which way a plate's outer face looks, in its group: front (+z), back (−z) or a side (±x). */
type Face = 'front' | 'back' | 'left' | 'right' | 'top';

/** What a look builds with: the colours, and the wear done to each plate it lays. */
export interface Kit {
  c: ArmourColours;
  wear: ArmourWear;
  rng: () => number;
  /** A plate: a box whose outer face takes the wear — worn, scuffs on its face and chipped corners; broken, a big one cracked through with a strike in it, a small one tilted loose. */
  plate(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number, face: Face): void;
  /** A soft piece (cloth, a pad): worn, a scrape; broken, a big one torn (a flap hanging), a small one gone. */
  pad(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number, face: Face): void;
}

function faceAxes(face: Face): { n: [number, number, number]; u: 0 | 1 | 2; v: 0 | 1 | 2; t: 0 | 1 | 2 } {
  // n: outward normal; u, v: the face's in-plane axes; t: the thickness axis
  if (face === 'front') return { n: [0, 0, 1], u: 0, v: 1, t: 2 };
  if (face === 'back') return { n: [0, 0, -1], u: 0, v: 1, t: 2 };
  if (face === 'top') return { n: [0, 1, 0], u: 0, v: 2, t: 1 };
  return { n: [face === 'right' ? 1 : -1, 0, 0], u: 2, v: 1, t: 0 };
}

export function makeKit(c: ArmourColours, wear: ArmourWear, seed: number): Kit {
  const rng = mulberry32(seed);
  /** A thin mark on a face: at (fu, fv) of its half-sizes, `su` × `sv` big, proud of it by a hair. */
  const mark = (parent: THREE.Object3D, size: number[], at: number[], face: Face, fu: number, fv: number, su: number, sv: number, color: string): THREE.Mesh => {
    const { n, u, v, t } = faceAxes(face);
    const dims = [0, 0, 0]; dims[u] = su; dims[v] = sv; dims[t] = 0.008;
    const pos = [...at]; pos[u] += fu * size[u] / 2; pos[v] += fv * size[v] / 2;
    for (let i = 0; i < 3; i++) pos[i] += n[i] * (size[t] / 2 + 0.003);
    return box(parent, dims[0], dims[1], dims[2], color, pos[0], pos[1], pos[2]);
  };
  const scuffs = (parent: THREE.Object3D, size: number[], at: number[], face: Face, n: number) => {
    const { u, v } = faceAxes(face);
    for (let i = 0; i < n; i++) {
      // scrapes run long and thin; chips sit at the edges
      const edge = rng() < 0.5, su = Math.min(size[u] * 0.8, 0.03 + rng() * 0.07), sv = 0.012 + rng() * 0.02;
      const fu = edge ? (rng() < 0.5 ? -1 : 1) * (1 - su / size[u]) : (rng() * 2 - 1) * 0.6, fv = edge ? (rng() * 2 - 1) * 0.8 : (rng() * 2 - 1) * 0.6;
      const m = mark(parent, size, at, face, fu, fv, su, sv, c.scuff);
      if (!edge) m.rotation[faceAxes(face).t === 2 ? 'z' : faceAxes(face).t === 0 ? 'x' : 'y'] = (rng() - 0.5) * 1.2;
    }
  };
  const plate: Kit['plate'] = (parent, w, h, d, color, x, y, z, face) => {
    const size = [w, h, d], at = [x, y, z], { u, v } = faceAxes(face), area = size[u] * size[v];
    if (wear === 'whole') { box(parent, w, h, d, color, x, y, z); return; }
    if (wear === 'worn') { box(parent, w, h, d, color, x, y, z); scuffs(parent, size, at, face, area > 0.04 ? 3 : 1); return; }
    if (area < 0.03) {   // broken, small: knocked askew on its strap
      const m = box(parent, w, h, d, color, x, y - 0.015, z);
      m.rotation.set((rng() - 0.5) * 0.5, 0, (rng() - 0.5) * 0.7);
      scuffs(parent, size, at, face, 1);
      return;
    }
    // broken, big: cracked through along u at a seeded place, the smaller part sagging, a strike near the crack
    const cut = (rng() * 2 - 1) * 0.3, gap = 0.012;
    const a = [...size], b = [...size], pa = [...at], pb = [...at];
    a[u] = size[u] * (1 + cut) / 2 - gap / 2; b[u] = size[u] * (1 - cut) / 2 - gap / 2;
    pa[u] = at[u] - size[u] / 2 + a[u] / 2; pb[u] = at[u] + size[u] / 2 - b[u] / 2;
    box(parent, a[0], a[1], a[2], color, pa[0], pa[1], pa[2]);
    const sag = box(parent, b[0], b[1], b[2], color, pb[0], pb[1] - 0.012, pb[2]);
    const tilt = faceAxes(face).t === 2 ? 'z' : faceAxes(face).t === 0 ? 'x' : 'y';
    sag.rotation[tilt] = (face === 'left' || face === 'back' ? 1 : -1) * 0.12;
    const fu = cut + (rng() < 0.5 ? -0.12 : 0.12), fv = (rng() * 2 - 1) * 0.4;
    mark(parent, size, at, face, fu, fv, 0.06, 0.06, c.scuff);           // the strike's splash
    mark(parent, size, at, face, fu, fv, 0.03, 0.03, c.crack).position.addScaledVector(new THREE.Vector3(...faceAxes(face).n), 0.002);   // its hole
    mark(parent, size, at, face, cut, (rng() - 0.5) * 0.3, 0.01, size[v] * 0.7, c.crack);   // the crack's dark line
    scuffs(parent, size, at, face, 2);
  };
  const pad: Kit['pad'] = (parent, w, h, d, color, x, y, z, face) => {
    const size = [w, h, d], at = [x, y, z], { u, v } = faceAxes(face), area = size[u] * size[v];
    if (wear === 'broken' && area < 0.02 && rng() < 0.5) return;           // torn off
    box(parent, w, h, d, color, x, y, z);
    if (wear === 'worn' && area > 0.03) scuffs(parent, size, at, face, 1);
    if (wear === 'broken' && area >= 0.02) {   // a torn flap hanging from its lower edge
      const flap = mark(parent, size, at, face, (rng() * 2 - 1) * 0.4, -0.85, Math.min(0.1, size[u] * 0.4), 0.09, color);
      flap.position.y -= 0.04; flap.rotation.x = 0.35;
    }
  };
  return { c, wear, rng, plate, pad };
}

// ---- wear on a helmet --------------------------------------------------------------------------------------------

/** The body's groups a look dresses (the model's own), with the strong side out (+x as built; 67.21's mirror flips the root). */
interface Body { torso: THREE.Group; head: THREE.Group; arms: Array<{ group: THREE.Group; fore: THREE.Group; out: number }>; thighs: THREE.Group[]; shins: THREE.Group[] }

/** The helmet's visor (the flash) shattered: a dark pane over it with one shard of flash left. */
function shatterVisor(head: THREE.Group, c: ArmourColours, rng: () => number): void {
  box(head, 0.2, 0.05, 0.012, c.crack, 0, 0.11, 0.186);
  const s = box(head, 0.05, 0.03, 0.012, c.patch, (rng() - 0.5) * 0.12, 0.115, 0.19); s.rotation.z = 0.5;
}
/** A helmet split: a dark crack over the shell, a bit of it gone at the rim. */
export function crackShell(head: THREE.Group, r: number, y: number, c: ArmourColours, rng: () => number): void {
  const side = rng() < 0.5 ? -1 : 1;
  const crack = new THREE.Mesh(new THREE.TorusGeometry(r + 0.004, 0.009, 3, 6, 1.0), mat(c.crack));
  crack.position.set(0, y, -0.01); crack.rotation.set(0, side * 0.9, 0.4 + rng() * 0.5); head.add(crack);
  box(head, 0.07, 0.04, 0.05, c.crack, side * (r - 0.02), y + 0.02, 0.06);
}
const Z = new THREE.Vector3(0, 0, 1);
export function helmetWear(k: Kit, head: THREE.Group, r: number, y: number): void {
  if (k.wear === 'worn') for (let i = 0; i < 3; i++) {
    const a = k.rng() * Math.PI * 2, e = 0.3 + k.rng() * 0.8;
    const m = box(head, 0.05 + k.rng() * 0.04, 0.014, 0.008, k.c.scuff, Math.sin(a) * Math.cos(e) * (r + 0.004), y + Math.sin(e) * (r + 0.004), -0.01 + Math.cos(a) * Math.cos(e) * (r + 0.004));
    m.quaternion.setFromUnitVectors(Z, m.position.clone().sub(new THREE.Vector3(0, y, -0.01)).normalize()); m.rotateZ(k.rng() * 2);
  }
  if (k.wear === 'broken') { crackShell(head, r, y, k.c, k.rng); shatterVisor(head, k.c, k.rng); }
}


// ---- the head -----------------------------------------------------------------------------------------------------

/** The bare head's parts (personModel.ts buildPersonModel), known by their built sizes: what a balaclava hides. */
type HeadPart = 'visor' | 'visorWrap' | 'faceGuard' | 'cup';
function headPart(o: THREE.Object3D): HeadPart | null {
  const g = (o as THREE.Mesh).geometry as (THREE.BufferGeometry & { parameters?: Record<string, number> }) | undefined;
  if (!g?.parameters) return null;
  const q = g.parameters, is = (w: number, h: number, d: number) => g.type === 'BoxGeometry' && q.width === w && q.height === h && q.depth === d;
  if (is(0.24, 0.07, 0.06)) return 'visor';
  if (is(0.04, 0.06, 0.08)) return 'visorWrap';
  if (is(0.18, 0.08, 0.08)) return 'faceGuard';
  if (is(0.05, 0.09, 0.09)) return 'cup';
  return null;
}
/** Hide the bare head's parts of these kinds (the bake skips what is hidden). */
function hideHead(head: THREE.Group, parts: HeadPart[]): void {
  for (const o of head.children) { const k = headPart(o); if (k && parts.includes(k)) o.visible = false; }
}

/** No helmet: a knit hood over the head, a neck tube, the eye slit — no visor, no comms cups. */
function balaclava(head: THREE.Group): void {
  hideHead(head, ['visor', 'visorWrap', 'faceGuard', 'cup']);
  const hood = new THREE.Mesh(new THREE.SphereGeometry(0.165, 7, 5), mat(BALACLAVA.knit));
  hood.position.set(0, 0.125, 0); hood.scale.set(1, 1.08, 1.02); head.add(hood);
  box(head, 0.15, 0.1, 0.15, BALACLAVA.knit, 0, 0.0, 0.0);                   // the neck tube over the neck
  box(head, 0.16, 0.042, 0.03, BALACLAVA.skin, 0, 0.13, 0.158);              // the eye slit
}

/** A helmet in the plate-carrier look (B): high-cut (light) or full-cut with a mandible guard (heavy); the leader's variant marks it subtly. */
function helmetB(k: Kit, head: THREE.Group, heavy: boolean, leader: boolean): void {
  // the light helmet is the old shell's size and place (r 0.19 at 0.14), so every figure measures as before (tests/hitShapes)
  const { c } = k, hr = heavy ? 0.212 : 0.19, hy = heavy ? 0.15 : 0.14;
  shell(head, hr, c.plate, hy);
  // (everything stays inside the old shell's envelope — its sphere, the neck guard's place at the back — so a figure hunched
  // in cover stands no taller than before: COVER_POSE_SIZE, which the sim reads, holds)
  band(head, Math.sqrt(hr * hr - 0.045 * 0.045), leader ? 0.012 : 0.01, leader ? c.patch : c.soft, hy + 0.045);   // the cover's band (the leader's in the flash)
  for (const sx of [-1, 1]) box(head, 0.02, 0.04, 0.15, c.trim, sx * (hr - 0.012), hy + 0.03, 0);     // the rails
  box(head, 0.08, 0.05, 0.035, c.trim, 0, hy + 0.1, hr - 0.03);                                // the mount at the front
  k.pad(head, 0.12, 0.08, 0.05, c.soft, 0, 0.1, -hr + 0.005, 'back');                           // the counterweight pouch, low at the back
  box(head, 0.035, 0.03, 0.012, c.patch, 0, 0.11, -hr - 0.026);                                   // the strobe, on the pouch's back
  if (heavy) {
    for (const sx of [-1, 1]) k.plate(head, 0.045, 0.16, 0.19, c.plate, sx * 0.205, hy - 0.06, 0, sx > 0 ? 'right' : 'left');   // over the ears
    k.plate(head, 0.2, 0.07, 0.05, c.plate, 0, 0.02, 0.175, 'front');                         // the mandible guard
    for (const sx of [-1, 1]) { const j = box(head, 0.04, 0.07, 0.12, c.plate, sx * 0.12, 0.02, 0.12); j.rotation.y = sx * 0.55; }
    if (leader) box(head, 0.2, 0.014, 0.054, c.patch, 0, -0.012, 0.175);                     // its edge in the flash
  }
  if (leader) leaderHelmetMarks(head, hr, hy, heavy, c);
  helmetWear(k, head, hr, hy);
}
/** The flak look's helmets (D): a covered helmet with goggles on the band (light), a pot with its face shield raised (heavy). */
function helmetD(k: Kit, head: THREE.Group, heavy: boolean, leader: boolean): void {
  const { c } = k, hr = heavy ? 0.222 : 0.2, hy = 0.14;
  shell(head, hr, heavy ? c.heavyPlate : c.soft, hy);
  band(head, Math.sqrt(hr * hr - 0.04 * 0.04) + 0.004, 0.016, leader ? c.patch : c.trim, hy + 0.04);
  if (!heavy) {
    for (const sx of [-1, 1]) box(head, 0.075, 0.05, 0.035, c.patch, sx * 0.05, hy + 0.06, hr - 0.005);   // the goggles' lenses
    box(head, 0.04, 0.025, 0.03, c.trim, 0, hy + 0.06, hr - 0.005);
  } else {
    const shield = box(head, 0.27, 0.13, 0.025, c.patch, 0, hy + 0.13, hr - 0.02); shield.rotation.x = -1.05;   // the face shield, raised
    for (const sx of [-1, 1]) box(head, 0.03, 0.05, 0.05, c.trim, sx * 0.15, hy + 0.06, 0.13);
    for (const sx of [-1, 1]) k.pad(head, 0.05, 0.12, 0.17, c.soft, sx * 0.2, 0.08, -0.02, sx > 0 ? 'right' : 'left');   // padded ear flaps
  }
  for (const sx of [-1, 1]) box(head, 0.008, 0.05, 0.05, c.patch, sx * (hr + 0.002), hy + 0.08, -0.04);   // the team's mark on each side
  if (leader) leaderHelmetMarks(head, hr, hy, heavy, c);
  helmetWear(k, head, hr, hy);
}
/** The leader's variant, on any helmet: a boom mic from the support-side cup, a short antenna on that side, two rank bars on the back. */
function leaderHelmetMarks(head: THREE.Group, hr: number, hy: number, heavy: boolean, c: ArmourColours): void {
  bar(head, [-0.17, 0.08, 0.02], [-0.07, 0.03, 0.17], 0.018, c.trim);
  box(head, 0.03, 0.025, 0.03, c.trim, -0.065, 0.03, 0.172);
  bar(head, [-hr + 0.01, hy + 0.06, -0.05], [-hr - 0.02, hy + 0.26, -0.09], 0.014, c.trim);
  for (const dy of [0.02, -0.01]) box(head, 0.07, 0.013, 0.012, c.patch, 0, hy + (heavy ? 0.13 : 0.12) + dy, -Math.sqrt(hr * hr - 0.1 * 0.1) - 0.012).userData.part = 'rank';
}

// ---- the vests ----------------------------------------------------------------------------------------------------

function vestB(b: Body, heavy: boolean, k: Kit): void {
  const { c } = k, t = b.torso;
  const pw = heavy ? 0.48 : 0.44, ph = heavy ? 0.42 : 0.36, pd = heavy ? 0.085 : 0.07;
  k.plate(t, pw, ph, pd, c.plate, 0, 0.37, 0.155 + pd / 2, 'front');
  k.plate(t, pw, ph, 0.06, c.plate, 0, 0.38, -0.17, 'back');
  for (const sx of [-1, 1]) {
    k.pad(t, 0.06, 0.17, 0.32, c.soft, sx * 0.27, 0.2, 0, sx > 0 ? 'right' : 'left');
    box(t, 0.1, 0.04, 0.36, c.soft, sx * 0.14, 0.645, 0);
    k.pad(t, 0.06, 0.1, 0.1, c.pouch, sx * 0.31, 0.21, 0.06, sx > 0 ? 'right' : 'left');
  }
  const front = 0.155 + pd;
  for (const x of [-0.12, 0, 0.12]) {   // the shingle: three magazines in the team's colour
    k.pad(t, 0.11, 0.14, 0.06, c.pouch, x, 0.27, front + 0.03, 'front');
    box(t, 0.115, 0.025, 0.065, c.trim, x, 0.345, front + 0.03);
  }
  k.pad(t, 0.22, 0.085, 0.035, c.soft, 0, 0.47, front + 0.017, 'front');                   // the admin pouch
  box(t, 0.08, 0.05, 0.01, c.patch, 0.05, 0.475, front + 0.04);                              // the team's patch on it
  if (!heavy) return;
  k.plate(t, 0.34, 0.08, 0.08, c.plate, 0, 0.63, 0.17, 'front');                             // the throat guard
  for (const sx of [-1, 1]) {
    box(t, 0.08, 0.08, 0.24, c.soft, sx * 0.17, 0.64, 0.04);
    k.plate(t, 0.05, 0.25, 0.22, c.heavyPlate, sx * 0.305, 0.33, 0, sx > 0 ? 'right' : 'left');   // side plates
  }
  const groin = box(t, 0.22, 0.18, 0.04, c.soft, 0, -0.07, 0.18); groin.rotation.x = -0.1;
  if (k.wear !== 'whole') groin.rotation.z = k.wear === 'broken' ? 0.25 : 0.06;
  for (const arm of b.arms) k.pad(arm.group, 0.2, 0.18, 0.22, c.soft, arm.out * 0.035, -0.08, 0, arm.out > 0 ? 'right' : 'left');   // deltoid pads
}

function vestD(b: Body, heavy: boolean, k: Kit): void {
  const { c } = k, t = b.torso;
  const vh = heavy ? 0.46 : 0.4, vy = heavy ? 0.34 : 0.36, vz = heavy ? 0.19 : 0.18;
  for (const sx of [-1, 1]) k.pad(t, 0.24, vh, 0.06, c.plate, sx * 0.125, vy, vz, 'front');
  box(t, 0.012, vh, 0.07, c.trim, 0, vy, vz);
  for (let i = 0; i < (heavy ? 6 : 5); i++) box(t, 0.5, 0.016, 0.07, c.trim, 0, vy - vh / 2 + 0.06 + i * 0.07, vz + 0.004);
  for (const sx of [-1, 1]) {
    k.pad(t, 0.05, vh - 0.04, 0.3, c.plate, sx * 0.27, vy, 0, sx > 0 ? 'right' : 'left');
    k.pad(t, 0.12, 0.1, 0.04, c.plate, sx * 0.13, vy - vh / 2 + 0.07, vz + 0.045, 'front');
    box(t, 0.125, 0.03, 0.045, c.trim, sx * 0.13, vy - vh / 2 + 0.12, vz + 0.045);
  }
  k.pad(t, 0.5, vh, 0.05, c.plate, 0, vy, -0.17, 'back');
  box(t, heavy ? 0.44 : 0.38, heavy ? 0.09 : 0.06, 0.3, c.plate, 0, 0.63, 0);               // the rolled collar
  box(t, 0.06, 0.04, 0.01, c.patch, -0.13, vy + vh / 2 - 0.06, vz + 0.032);
  if (!heavy) return;
  k.pad(t, 0.44, 0.16, 0.06, c.plate, 0, 0.03, 0.19, 'front');                               // the apron
  box(t, 0.44, 0.016, 0.065, c.trim, 0, 0.0, 0.192);
  for (const arm of b.arms) {
    k.pad(arm.group, 0.24, 0.18, 0.26, c.plate, arm.out * 0.03, -0.03, 0, arm.out > 0 ? 'right' : 'left');
    box(arm.group, 0.245, 0.016, 0.265, c.trim, arm.out * 0.03, -0.06, 0);
  }
  for (const th of b.thighs) k.pad(th, 0.23, 0.2, 0.06, c.plate, 0, -0.12, 0.13, 'front');
}

// ---- dressing a person --------------------------------------------------------------------------------------------

/** What a person wears: a helmet and a vest (free together), the look, the leader's kit, the wear, the colours. */
export interface KitDress { helmet: HelmetKind; vest: VestKind; look?: VestLook; leader?: boolean; wear?: ArmourWear; scheme?: ArmourScheme; seed?: number }

/**
 * Dress a person's full tier (its head, torso, arms, thighs and shins — groups that already move) before the bake;
 * `buildPersonModel` calls it with the kit. The far tier's poses are personModel.ts's. Returns the parts added.
 */
export function dressArmour(m: PersonModel, p: UnitPalette, d: KitDress): number {
  const look = d.look ?? 'B';
  const k = makeKit(armourColours(p, d.scheme ?? VEST_LOOKS[look].scheme), d.wear ?? 'whole', d.seed ?? 1);
  const body: Body = {
    torso: m.torso, head: m.head,
    arms: [m.armL, m.armR].map(a => ({ group: a.group, fore: a.fore, out: Math.sign(a.group.position.x) })),
    thighs: [m.thighL, m.thighR], shins: [m.shinL, m.shinR],
  };
  const before = count(m.full);
  if (d.helmet === 'none') balaclava(m.head);
  else (look === 'B' ? helmetB : helmetD)(k, m.head, d.helmet === 'heavy', d.leader === true);
  if (d.vest !== 'none') (look === 'B' ? vestB : vestD)(body, d.vest === 'heavy', k);
  if (d.leader) dressLeaderKit(m, p);
  return count(m.full) - before;
}

function count(o: THREE.Object3D): number { let n = 0; o.traverse(q => { if ((q as THREE.Mesh).isMesh) n++; }); return n; }

/**
 * The helmet a unit is drawn in (the owner, Oct 18: "1 sure no armor 2. real piece of armor"). With no armour in the
 * match (`GameConfig.armour` unset), every figure wears the light helmet, as every figure wore a helmet before. With
 * armour in play, the helmet it wears, else a balaclava: the light helmet light, the heavy one (190, lane C's
 * `heavyHelmet` kind) heavy; the vest never makes it heavy.
 */
export function drawnHelmet(armourInPlay: boolean, worn: ArmourWorn | undefined): HelmetKind {
  if (!armourInPlay) return 'light';
  if (!worn?.helmet) return 'none';
  // (190: the heavy helmet as a real piece; the host rebuilds the view when the drawn helmet changes)
  return worn.helmet.kind === 'heavyHelmet' ? 'heavy' : 'light';
}
